import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../firebaseConfig';
import { validateReportArea, type DailyAreaReport, type ReportArea } from '../../functions/src/common/dailyReportArea';

export function useDailyReportArea(country: string | null | undefined, organizationId?: string) {
    const uid = auth.currentUser?.uid;
    const key = uid && country && organizationId ? `${uid}_${organizationId}_${country}` : '';
    const [state, setState] = useState<{ key: string; area?: ReportArea; report?: DailyAreaReport; error?: string; refreshError?: string }>({ key: '' });
    const [attempt, setAttempt] = useState(0);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!key)
            return;
        return onSnapshot(doc(db, 'daily_report_preferences', key), snapshot => {
            try {
                const value = snapshot.data();
                if (value && value.area?.country !== country)
                    throw new Error('Saved area does not match this country.');
                setState({ key, area: value ? validateReportArea(value.area) : undefined, report: value?.report, refreshError: value?.refreshError });
            } catch {
                setState({ key, error: 'Your saved report area could not be loaded.' });
            }
        }, () => setState({ key, error: 'Your saved report area could not be loaded. Please try again.' }));
    }, [key, country, attempt]);
    const loaded = state.key === key;
    return {
        area: loaded && state.area || { country: country || '', subdivisionCodes: [] },
        report: loaded ? state.report : undefined,
        error: loaded ? state.error : undefined,
        refreshError: loaded ? state.refreshError : undefined,
        loading: !!key && !loaded,
        canSave: !!key,
        saving,
        save: async (area: ReportArea) => {
            setSaving(true);
            try {
                const response = await saveDailyReportArea(area);
                setState(previous => previous.key === key ? { key, area: response.area, report: response.report || undefined } : previous);
            } finally {
                setSaving(false);
            }
        },
        retry: () => { setState({ key: '' }); setAttempt(value => value + 1); },
    };
}

export async function saveDailyReportArea(area: ReportArea) {
    const save = httpsCallable<{ area: ReportArea }, { area: ReportArea; report: DailyAreaReport | null }>(getFunctions(undefined, 'us-central1'), 'saveDailyReportArea', { timeout: 120000 });
    return (await save({ area })).data;
}
